import type { EraDef } from '../content/types';

const TAU = Math.PI * 2;
const clamp = (v: number) => Math.max(0, Math.min(1, v));
const smooth = (a: number, b: number, v: number) => {
  const t = clamp((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
// Stable cosmetic variation: never consumes simulation RNG or depends on frame count.
const seed = (n: number) => { const v = Math.sin(n * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };
const COLORS = ['#170c28', '#032a3d', '#071e17', '#102f49', '#221008', '#241309', '#151125', '#11191f', '#040d25', '#110a22'];

/** Destination-specific, analytical transition choreography. The caller owns its clock. */
export class EraTransition {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private era = 0;
  private low = false;
  private active = false;
  private progress = 0;
  private width = 1;
  private height = 1;
  private readonly onResize = () => { if (this.active) this.update(this.progress); };

  constructor(canvasParent: HTMLElement = document.body) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'era-transition';
    this.canvas.setAttribute('aria-hidden', 'true');
    Object.assign(this.canvas.style, { position: 'fixed', inset: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '15', display: 'none' });
    canvasParent.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    window.addEventListener('resize', this.onResize);
  }

  start(era: EraDef, lowQuality: boolean): void {
    this.era = era.index;
    this.low = lowQuality;
    this.active = true;
    this.canvas.style.display = 'block';
    this.update(0);
  }

  update(progress: number): void {
    if (!this.active || !this.ctx) return;
    const t = this.progress = clamp(progress);
    const w = Math.max(1, window.innerWidth), h = Math.max(1, window.innerHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, this.low ? 1 : 2);
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) {
      this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr);
    }
    const c = this.ctx;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, w, h);
    // Wide and portrait viewports use the same central composition, with scenery extending outward.
    const scale = Math.min(w, h) / 800;
    c.translate(w / 2, h / 2); c.scale(scale, scale);
    this.width = w / scale; this.height = h / scale;
    c.save();
    c.globalAlpha = smooth(0, .16, t) * (1 - smooth(.72, 1, t));
    c.fillStyle = COLORS[this.era] ?? COLORS[0];
    c.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
    const halo = c.createRadialGradient(0, 0, 0, 0, 0, 600);
    halo.addColorStop(0, ['#663578', '#137c9b', '#47704a', '#74b6ca', '#a34b12', '#aa7c2e', '#664275', '#646b60', '#15467e', '#654170'][this.era] ?? '#663578');
    halo.addColorStop(1, 'transparent'); c.fillStyle = halo;
    c.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
    switch (this.era) {
      case 1: this.sea(c, t); break;
      case 2: this.jungle(c, t); break;
      case 3: this.frost(c, t); break;
      case 4: this.fire(c, t); break;
      case 5: this.gates(c, t); break;
      case 6: this.glass(c, t); break;
      case 7: this.industry(c, t); break;
      case 8: this.future(c, t); break;
      case 9: this.hourglass(c, t); break;
      default: this.hourglass(c, t); break;
    }
    c.restore();
  }

  clear(): void {
    this.active = false;
    this.canvas.style.display = 'none';
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  dispose(): void {
    this.clear(); window.removeEventListener('resize', this.onResize); this.canvas.remove();
  }

  private line(c: CanvasRenderingContext2D, points: number[], color: string, width = 2): void {
    c.beginPath(); c.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) c.lineTo(points[i], points[i + 1]);
    c.strokeStyle = color; c.lineWidth = width; c.stroke();
  }

  private sea(c: CanvasRenderingContext2D, t: number): void {
    c.save(); c.rotate(t * .85);
    for (let ring = 5; ring >= 0; ring--) {
      c.beginPath();
      const r = 80 + ring * 86 + Math.sin(t * Math.PI) * 90;
      for (let k = 0; k <= 96; k++) {
        const a = k / 96 * TAU, wave = r + Math.sin(a * 3 + t * 8 + ring) * 25;
        const x = Math.cos(a) * wave * 1.3, y = Math.sin(a) * wave;
        k ? c.lineTo(x, y) : c.moveTo(x, y);
      }
      c.closePath(); c.strokeStyle = ['#a7f4e1', '#65d4dc', '#1fb4c9'][ring % 3];
      c.globalAlpha *= .78; c.lineWidth = 18 + ring * 8; c.stroke();
    }
    c.restore();
    for (let i = 0; i < (this.low ? 24 : 50); i++) {
      const x = (seed(i) - .5) * this.width, y = ((seed(i + 100) + 1 - t * .8) % 1) * this.height - this.height / 2;
      const r = 3 + seed(i + 30) * 13;
      c.beginPath(); c.arc(x + Math.sin(t * 5 + i) * 18, y, r, 0, TAU);
      c.strokeStyle = '#adebe6'; c.lineWidth = 1.5; c.stroke();
    }
    // Branching coral fans along the sea-floor rim.
    for (let i = 0; i < 9; i++) {
      const x = (i / 8 - .5) * this.width, y = this.height / 2 + 20;
      for (let b = -2; b <= 2; b++) {
        const tipX = x + b * 28, tipY = y - 75 - seed(i + b + 80) * 90;
        this.line(c, [x, y, x, y - 45, tipX, tipY], i % 2 ? '#d78887' : '#64bdae', 7);
        this.line(c, [tipX, tipY + 24, tipX + 18, tipY - 12], '#d9a399', 4);
      }
    }
  }

  private jungle(c: CanvasRenderingContext2D, t: number): void {
    // Fern fronds bow outwards as the camera falls through the canopy.
    for (let i = 0; i < 14; i++) {
      c.save(); c.rotate(i / 14 * TAU + Math.sin(t * 3 + i) * .04);
      c.translate(0, -470 - smooth(.4, 1, t) * 150);
      c.rotate((i % 2 ? 1 : -1) * .22);
      this.line(c, [0, 0, 15, 150, 0, 370], '#7c9860', 4);
      for (let j = 0; j < 10; j++) {
        const y = j * 32, breadth = Math.sin((j + 1) / 12 * Math.PI) * 105;
        for (const side of [-1, 1]) {
          c.beginPath(); c.moveTo(7, y + 35);
          c.quadraticCurveTo(side * breadth, y - 18, side * breadth, y - 42);
          c.quadraticCurveTo(side * breadth * .45, y + 29, 7, y + 35);
          c.fillStyle = i % 3 ? '#163d28' : '#2b5735'; c.fill();
        }
      }
      c.restore();
    }
    c.save(); c.translate((t - .5) * 1000, -50 + Math.sin(t * 4) * 25); c.scale(1.4, 1.4);
    c.fillStyle = '#04110dd9'; c.beginPath();
    c.moveTo(-220, 30); c.quadraticCurveTo(-120, 35, -65, -30);
    c.quadraticCurveTo(-15, -60, 55, -15); c.lineTo(77, -85);
    c.quadraticCurveTo(100, -115, 145, -86); c.lineTo(150, -60); c.lineTo(99, -54);
    c.lineTo(74, 29); c.lineTo(45, 47); c.lineTo(25, 100); c.lineTo(7, 100);
    c.lineTo(11, 43); c.lineTo(-43, 39); c.lineTo(-65, 94); c.lineTo(-84, 94);
    c.lineTo(-75, 28); c.quadraticCurveTo(-140, 66, -220, 30); c.fill(); c.restore();
    for (let i = 0; i < 25; i++) {
      c.fillStyle = '#c2cf8144'; c.beginPath();
      c.ellipse((seed(i) - .5) * this.width, (seed(i + 60) - .5) * this.height + t * 70, 2, 4, t + i, 0, TAU); c.fill();
    }
  }

  private frost(c: CanvasRenderingContext2D, t: number): void {
    const reach = 70 + smooth(0, .55, t) * 370;
    for (let i = 0; i < 16; i++) {
      c.save(); c.rotate(i / 16 * TAU);
      this.line(c, [0, 0, 0, reach], '#bcecff', 3);
      for (let j = 1; j < 7; j++) {
        const y = j * reach / 8, branch = (1 - j / 8) * 85;
        for (const side of [-1, 1]) {
          this.line(c, [0, y, side * branch, y - branch * .8], '#9fd5ed', 2);
          this.line(c, [side * branch * .55, y - branch * .44, side * branch * .55, y - branch], '#e1faff', 1);
        }
      }
      c.restore();
    }
    for (let i = 0; i < (this.low ? 65 : 150); i++) {
      const x = (seed(i) - .5) * this.width + Math.sin(t * 3 + i) * 45;
      const y = ((seed(i + 180) + t * (.15 + seed(i + 20) * .35)) % 1 - .5) * this.height;
      c.fillStyle = '#e9fbff'; c.beginPath(); c.arc(x, y, 1 + seed(i + 80) * 4, 0, TAU); c.fill();
    }
  }

  private fire(c: CanvasRenderingContext2D, t: number): void {
    const ignite = smooth(.02, .32, t);
    c.save(); c.translate(0, 80); c.scale(ignite, ignite);
    this.line(c, [-110, 50, 105, 80], '#6b3920', 21);
    this.line(c, [-92, 80, 100, 45], '#94603b', 17);
    for (let i = 0; i < 5; i++) {
      const x = (i - 2) * 24, height = 100 + Math.sin(i * 2 + t * 17) * 35;
      c.beginPath(); c.moveTo(x - 33, 50);
      c.bezierCurveTo(x - 80, -20, x + 35, -height, x + Math.sin(t * 16 + i) * 18, -height - 35);
      c.bezierCurveTo(x + 70, -25, x + 53, 24, x + 30, 50);
      c.fillStyle = ['#ce4a15', '#f87516', '#ffd379', '#ffae35', '#df511a'][i]; c.fill();
    }
    c.restore();
    for (let i = 0; i < (this.low ? 50 : 110); i++) {
      const life = (seed(i + 80) + t * 1.3) % 1;
      const a = i * 2.4 + t * 6, r = life * 310;
      const x = Math.cos(a) * r, y = 135 - life * 780;
      c.save(); c.translate(x, y); c.rotate(a);
      c.fillStyle = life > .7 ? '#f27229' : '#ffcf75'; c.fillRect(-2, -3, 3, 6 + seed(i) * 5); c.restore();
    }
  }

  private gates(c: CanvasRenderingContext2D, t: number): void {
    const open = smooth(.32, .9, t), shift = open * (this.width / 2 + 140);
    const beam = c.createLinearGradient(-180, 0, 180, 0);
    beam.addColorStop(0, 'transparent'); beam.addColorStop(.5, '#ffe6a8'); beam.addColorStop(1, 'transparent');
    c.fillStyle = beam; c.fillRect(-180, -this.height / 2, 360, this.height);
    for (const side of [-1, 1]) {
      c.save(); c.scale(side, 1); c.translate(shift, 0);
      const metal = c.createLinearGradient(0, 0, this.width / 2, 0);
      metal.addColorStop(0, '#d7ac62'); metal.addColorStop(.04, '#4c301a'); metal.addColorStop(.45, '#98713e'); metal.addColorStop(1, '#37271a');
      c.fillStyle = metal; c.fillRect(0, -this.height / 2, this.width / 2 + 50, this.height);
      c.strokeStyle = '#dda961'; c.lineWidth = 6; c.strokeRect(20, -this.height / 2 + 25, this.width / 2 - 45, this.height - 50);
      for (let row = -3; row <= 3; row++) {
        const y = row * 115;
        this.line(c, [48, y + 30, 48, y - 30, 90, y - 30, 90, y + 12, 70, y + 12, 70, y - 10], '#e4b774', 3);
        for (let col = 0; col < 3; col++) {
          const x = 145 + col * 90;
          this.line(c, [x - 25, y, x, y - 28, x + 25, y, x, y + 28, x - 25, y], '#4a311d', 5);
          c.fillStyle = '#dbb472'; c.beginPath(); c.arc(x, y, 4, 0, TAU); c.fill();
        }
      }
      c.strokeStyle = '#f0c377'; c.lineWidth = 7; c.beginPath(); c.ellipse(36, 40, 12, 25, 0, 0, TAU); c.stroke();
      c.restore();
    }
  }

  private glass(c: CanvasRenderingContext2D, t: number): void {
    const burst = smooth(.38, .94, t), radius = 310;
    c.save(); c.rotate(-.15 + t * .3);
    const colors = ['#386fa3', '#ac486c', '#c99436', '#487e74', '#8561a9', '#438cac'];
    for (let ring = 0; ring < 3; ring++) {
      for (let i = 0; i < 12; i++) {
        const a = i / 12 * TAU, mid = a + TAU / 24;
        c.save(); c.translate(Math.cos(mid) * burst * (250 + ring * 160), Math.sin(mid) * burst * (250 + ring * 160));
        c.rotate((seed(i + ring * 20) - .5) * burst * .7);
        c.beginPath(); c.arc(0, 0, (ring + 1) * radius / 3, a + .016, a + TAU / 12 - .016);
        c.arc(0, 0, ring * radius / 3 + 4, a + TAU / 12 - .016, a + .016, true); c.closePath();
        c.fillStyle = colors[(i + ring * 2) % colors.length]; c.fill(); c.strokeStyle = '#d7bd83'; c.lineWidth = 3; c.stroke();
        c.restore();
      }
    }
    c.strokeStyle = '#d7bd83'; c.lineWidth = 10; c.beginPath(); c.arc(0, 0, radius * (1 + burst * 2), 0, TAU); c.stroke(); c.restore();
  }

  private gear(c: CanvasRenderingContext2D, x: number, y: number, radius: number, angle: number): void {
    c.save(); c.translate(x, y); c.rotate(angle); c.beginPath();
    for (let k = 0; k < 64; k++) {
      const a = k / 64 * TAU, r = radius * (k % 4 < 2 ? 1 : .84);
      k ? c.lineTo(Math.cos(a) * r, Math.sin(a) * r) : c.moveTo(r, 0);
    }
    c.closePath(); c.fillStyle = '#4f5a5c'; c.fill(); c.strokeStyle = '#bdac83'; c.lineWidth = 3; c.stroke();
    c.beginPath(); c.arc(0, 0, radius * .6, 0, TAU); c.fillStyle = '#162025'; c.fill(); c.stroke();
    for (let i = 0; i < 6; i++) {
      const a = i / 6 * TAU; this.line(c, [Math.cos(a) * 20, Math.sin(a) * 20, Math.cos(a) * radius * .66, Math.sin(a) * radius * .66], '#6f7770', 17);
    }
    c.beginPath(); c.arc(0, 0, 20, 0, TAU); c.fillStyle = '#bca579'; c.fill(); c.restore();
  }

  private industry(c: CanvasRenderingContext2D, t: number): void {
    const engage = 1 - smooth(.03, .32, t);
    this.gear(c, -220 - engage * 250, -90, 180, t * 3);
    this.gear(c, 74 + engage * 250, -90, 142, -t * 3.75);
    this.gear(c, 250, 150 + engage * 250, 170, t * 3.15);
    for (const side of [-1, 1]) {
      const y = Math.sin(t * TAU * 2) * 65;
      this.line(c, [side * 300, 420, side * 300, 160 + y], '#a5ada7', 18);
      c.fillStyle = '#626c6b'; c.fillRect(side * 300 - 40, 130 + y, 80, 85);
      this.line(c, [side * 300 - 40, 150 + y, side * 300 + 40, 150 + y], '#d1b57e', 4);
    }
    for (let i = 0; i < (this.low ? 16 : 32); i++) {
      const life = (t * .9 + seed(i)) % 1;
      c.beginPath(); c.arc((seed(i + 80) - .5) * this.width + Math.sin(i + t * 3) * 60, 420 - life * 900, 30 + life * 100, 0, TAU);
      c.fillStyle = `rgba(194,213,212,${.1 * (1 - life)})`; c.fill();
    }
  }

  private future(c: CanvasRenderingContext2D, t: number): void {
    const horizon = -130, sweep = -this.height / 2 + t * this.height;
    for (let i = -14; i <= 14; i++) this.line(c, [i * 16, horizon, i * 150, this.height / 2], '#21838a', 1.5);
    for (let i = 0; i < 15; i++) {
      const z = ((i / 15 + t * .3) % 1) ** 2;
      const y = horizon + z * (this.height / 2 - horizon);
      this.line(c, [-this.width / 2, y, this.width / 2, y], '#3cb5b5', 1.5);
    }
    for (let i = 0; i < 28; i++) {
      const x = (i - 14) * 46, top = horizon - 40 - seed(i) * 200;
      const reveal = smooth(.05 + seed(i + 10) * .25, .6, t);
      c.fillStyle = '#0b2340'; c.strokeStyle = i % 3 ? '#39c7d9' : '#bc69f2'; c.lineWidth = 1.5;
      c.fillRect(x, horizon - (horizon - top) * reveal, 33, (horizon - top) * reveal);
      c.strokeRect(x, horizon - (horizon - top) * reveal, 33, (horizon - top) * reveal);
      for (let j = 0; j < 5; j++) if (top + j * 22 > horizon - (horizon - top) * reveal) this.line(c, [x + 8, top + j * 22, x + 24, top + j * 22], '#69d6e3', 2);
    }
    const scan = c.createLinearGradient(0, sweep - 55, 0, sweep + 12);
    scan.addColorStop(0, 'transparent'); scan.addColorStop(.85, '#46ddec33'); scan.addColorStop(1, '#a5fffb');
    c.fillStyle = scan; c.fillRect(-this.width / 2, sweep - 55, this.width, 67);
    for (let y = -this.height / 2; y < this.height / 2; y += 6) this.line(c, [-this.width / 2, y, this.width / 2, y], '#7ad1eb0c', 1);
  }

  private hourglass(c: CanvasRenderingContext2D, t: number): void {
    c.save(); c.rotate(Math.sin(t * Math.PI) * .08);
    // The hourglass empties backwards, hangs at the apex, then falls forward again.
    const reverse = t < .42 ? smooth(0, .42, t) : t < .56 ? 1 : 1 - smooth(.56, 1, t);
    c.strokeStyle = '#c9ad80'; c.lineWidth = 7;
    c.beginPath(); c.moveTo(-138, -238); c.bezierCurveTo(-145, -115, -28, -35, -12, 0);
    c.bezierCurveTo(-28, 35, -145, 115, -138, 238); c.lineTo(138, 238);
    c.bezierCurveTo(145, 115, 28, 35, 12, 0); c.bezierCurveTo(28, -35, 145, -115, 138, -238); c.closePath(); c.stroke();
    for (const side of [-1, 1]) {
      this.line(c, [-166, side * 247, 166, side * 247], '#d2b990', 17);
      this.line(c, [side * 162, -237, side * 162, 237], '#715874', 6);
    }
    for (let i = 0; i < (this.low ? 75 : 150); i++) {
      const s = seed(i), f = clamp(reverse * 1.7 - s * .7);
      const bottomY = 220 - seed(i + 50) * 70, topY = -220 + seed(i + 50) * 70;
      const y = bottomY + (topY - bottomY) * f;
      const width = 8 + Math.abs(y) * .43;
      const x = (seed(i + 200) - .5) * width * 2;
      c.save(); c.translate(x, y); c.rotate(i + reverse * 4);
      c.fillStyle = ['#e6c998', '#ae98cd', '#74bfc3'][i % 3]; c.fillRect(-2, -2, 4 + s * 3, 4 + s * 3); c.restore();
    }
    c.restore();
    for (let i = 0; i < 18; i++) {
      const a = i / 18 * TAU + t * .25, r = 340 + seed(i) * 180;
      const x = Math.cos(a) * r, y = Math.sin(a) * r;
      c.save(); c.translate(x, y); c.rotate(a + reverse);
      this.line(c, [-16, -12, 22, -17, 11, 21, -16, -12], '#a696ba77', 2); c.restore();
    }
  }
}
